import {
  BillingAccess,
  BillingInterval,
  BillingPlanOption,
  BillingStatus,
  PlanKey,
  SubscriptionStatus,
  TrialSummary,
} from '../models/billing.interface';

/**
 * Everything the billing screens say about a subscription, in one place and free of Angular, so each
 * sentence can be tested by handing it a value. The banner and the Billing page both read from here,
 * so they never describe the same state in two different ways.
 */

export const PLAN_LABELS: Readonly<Record<PlanKey, string>> = {
  SOLO: 'Solo',
  WORKSHOP: 'Workshop',
  WORKSHOP_PLUS: 'Workshop Plus',
};

export const INTERVAL_LABELS: Readonly<Record<BillingInterval, string>> = {
  MONTH: 'Monthly',
  YEAR: 'Yearly',
};

export type DisplaySeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

export const STATUS_DISPLAY: Readonly<
  Record<SubscriptionStatus, { label: string; severity: DisplaySeverity }>
> = {
  TRIALING: { label: 'Free trial', severity: 'info' },
  ACTIVE: { label: 'Active', severity: 'success' },
  PAST_DUE: { label: 'Payment failed', severity: 'warn' },
  UNPAID: { label: 'Unpaid', severity: 'danger' },
  PAUSED: { label: 'Trial ended', severity: 'danger' },
  CANCELED: { label: 'Cancelled', severity: 'secondary' },
};

/** What the workshop may do, in words staff understand. LOCKED is the read-only state from the billing lock. */
export const ACCESS_DISPLAY: Readonly<
  Record<BillingAccess, { label: string; severity: DisplaySeverity }>
> = {
  FULL: { label: 'Full access', severity: 'success' },
  FULL_WITH_WARNING: { label: 'Full access, payment warning', severity: 'warn' },
  LOCKED: { label: 'Read-only', severity: 'danger' },
};

/**
 * Whether a link from the API may be put on a button that opens a new tab. The API builds these links itself, but
 * a link that is not a Stripe Dashboard address is never worth following, so anything else is dropped.
 */
export function isStripeDashboardUrl(url: string | null): url is string {
  return url !== null && url.startsWith('https://dashboard.stripe.com/');
}

export interface BillingBanner {
  severity: 'info' | 'warn' | 'error';
  message: string;
}

/** The banner for the top of every screen, or null when nothing needs saying. */
export function describeBillingBanner(
  billing: BillingStatus | null,
  now: Date = new Date()
): BillingBanner | null {
  if (!billing) return null;

  switch (billing.status) {
    case 'TRIALING':
      return {
        severity: 'info',
        message: `${trialSentence(billing.trialEndsAt, now)} Add a payment method to keep using Greezmate.`,
      };
    case 'PAST_DUE':
      return {
        severity: 'warn',
        message:
          "We couldn't take your last payment. Please update your payment method to avoid losing access.",
      };
    case 'UNPAID':
      return {
        severity: 'error',
        message:
          'Your subscription is unpaid. Please update your payment method to restore access.',
      };
    case 'PAUSED':
      return {
        severity: 'error',
        message: 'Your free trial has ended. Add a payment method to continue using Greezmate.',
      };
    case 'CANCELED':
      return {
        severity: 'error',
        message: 'Your subscription has ended. Please contact Greezmate support to restart it.',
      };
    case 'ACTIVE':
      return null;
  }
}

/** "Your free trial ends in 3 days (31 Oct 2026)." */
export function trialSentence(trialEndsAt: string | null, now: Date = new Date()): string {
  if (!trialEndsAt) return 'Your free trial is running.';
  const days = daysUntil(trialEndsAt, now);
  const date = formatDate(trialEndsAt);
  if (days <= 0) return `Your free trial ends today (${date}).`;
  return `Your free trial ends in ${days} ${days === 1 ? 'day' : 'days'} (${date}).`;
}

/** Whole days from now until the date, rounded up, so 2.1 days left reads as 3. */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const ms = new Date(iso).getTime() - now.getTime();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** The line under the status on the Billing page: what happens next, in plain words. */
export function describeNextStep(billing: BillingStatus, now: Date = new Date()): string {
  switch (billing.status) {
    case 'TRIALING':
      return `${trialSentence(billing.trialEndsAt, now)} After that you are charged, if a payment method is on file.`;
    case 'ACTIVE':
      if (billing.cancelAtPeriodEnd && billing.currentPeriodEnd) {
        return `Your subscription ends on ${formatDate(billing.currentPeriodEnd)} and will not renew.`;
      }
      return billing.currentPeriodEnd
        ? `Your next payment is on ${formatDate(billing.currentPeriodEnd)}.`
        : 'Your subscription is active.';
    case 'PAST_DUE':
      return "We couldn't take your last payment and will try again. Update your payment method to avoid losing access.";
    case 'UNPAID':
      return 'Your subscription is unpaid. Update your payment method to restore access.';
    case 'PAUSED':
      return 'Your free trial has ended and no payment method was added. Add one to continue.';
    case 'CANCELED':
      return 'Your subscription has ended. Please contact Greezmate support to restart it.';
  }
}

/** "Changes to Workshop on 17 Nov 2026." when a downgrade is scheduled, otherwise null. */
export function describePendingChange(billing: BillingStatus): string | null {
  if (!billing.pendingPlanKey || !billing.pendingChangeAt) return null;
  return `Changes to ${PLAN_LABELS[billing.pendingPlanKey]} on ${formatDate(billing.pendingChangeAt)}.`;
}

/**
 * The label for the button that opens Stripe's page for managing billing, or null when that page cannot help.
 * Until a card is on file and paying, the useful action is adding one; once active it is general
 * management. A cancelled subscription cannot be restarted there, so no button.
 */
export function manageButtonLabel(status: SubscriptionStatus): string | null {
  switch (status) {
    case 'TRIALING':
    case 'PAUSED':
    case 'PAST_DUE':
    case 'UNPAID':
      return 'Add payment method';
    case 'ACTIVE':
      return 'Manage billing';
    case 'CANCELED':
      return null;
  }
}

/**
 * Whether "Pay now" makes sense: only when a payment is overdue. Then the owner can fix the card on
 * Stripe's page and pay straight away, instead of waiting for the next automatic retry.
 */
export function canPayNow(status: SubscriptionStatus): boolean {
  return status === 'PAST_DUE' || status === 'UNPAID';
}

/** "$79.00/month + GST": a plan's price for one billing interval. The API sends NZD cents, excluding GST. */
export function formatPlanPrice(plan: BillingPlanOption, interval: BillingInterval): string {
  const cents = interval === 'MONTH' ? plan.monthlyCents : plan.yearlyCents;
  const unit = interval === 'MONTH' ? 'month' : 'year';
  return `${NZD.format(cents / 100)}/${unit} + GST`;
}

/** "Free trial (Workshop, monthly) ends 6 Nov 2026. Invite sent to owner@example.com." for staff after creating a tenant. */
export function describeNewTrial(billing: TrialSummary, invitedAdminEmail: string): string {
  const plan = `${PLAN_LABELS[billing.planKey]}, ${INTERVAL_LABELS[billing.billingInterval].toLowerCase()}`;
  const when = billing.trialEndsAt ? `ends ${formatDate(billing.trialEndsAt)}` : 'started';
  return `Free trial (${plan}) ${when}. Invite sent to ${invitedAdminEmail}.`;
}

const NZD = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' });
